<template>
  <div class="characters-page">
    <sp-tab active="characters" :hidden="!!characterId" />
    <list-template v-if="characters" :active="!!characterId" :selected="selectedCharacter">
      <div slot="side" v-if="characterFolders && isShowFolderList">
        <character-folder-list
          :novelId="novelId"
          :items="characterFolders"
          :charactersCount="charactersCount"
          :isInsert="isFolderInsertMode"
          :selectedCharacter="targetCharacter"
          @selectedFolder="selectFolder"
          @clickCompleteSetFolder="finishFolderSelect()"
        />
      </div>
      <div slot="side" v-else>
        <character-list
          slot="side"
          :novelId="novelId"
          :characters="characters"
          :selectedId="selectedCharacter && selectedCharacter.characterId"
          :folder="characterFolder"
          :allFolders="characterFolders"
          @backToFolderList="showFolderList()"
          @clickSetFolder="setFolder"
        />
      </div>
      <back-to-list slot="back-to-list" :to="{ name: 'characters', params: { novelId } }">{{
        $t("characterPage.backToList")
      }}</back-to-list>
      <character-detail
        v-if="selectedCharacter"
        slot="main"
        :novelId="novelId"
        :character="selectedCharacter"
        :editor-menu-visible="true"
        @showHistoryDialog="isHistoryManageDialogOpen = true"
      />
    </list-template>
    <history-manage-dialog
      v-if="selectedCharacter"
      :is-open="isHistoryManageDialogOpen"
      data-type="ch"
      :title="$t('historyManageDialog.title.characterDetail') + ' - ' + (selectedCharacter.name || 'No Name')"
      :novel-id="novelId"
      :data-id="selectedCharacter.characterId"
      @close="isHistoryManageDialogOpen = false"
      @restore="onCharacterRestore"
    >
      <template v-slot:notification> {{ $t('historyManageDialog.notification.localImageExcluded') }} </template>
      <template v-slot:content="{ historyItem }">
        <character-detail
          v-if="historyItem"
          :novel-id="novelId"
          :character="{
            ...historyItem.item,
            characterId: selectedCharacter.characterId,
          }"
          :editor-menu-visible="false"
        />
      </template>
    </history-manage-dialog>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ListTemplate from "@/components/templates/ListTemplate.vue";
import CharacterList from "@/components/organisms/CharacterList.vue";
import CharacterFolderList from "@/components/organisms/CharacterFolderList.vue";
import CharacterDetail from "@/components/organisms/CharacterDetail.vue";
import HistoryManageDialog from "@/components/ui/dialogs/HistoryManageDialog.vue";
import SpTab from "@/components/molecules/SpTab.vue";
import BackToList from "@/components/atoms/BackToList.vue";
import { Character, CharacterFolder, Plan } from "@/lib/models";
import { cloneDeep } from "lodash";
import {
  hasHistoryQuery,
  omitHistoryQuery,
  shouldRedirectEmptyListToCreate,
} from "@/lib/utils/deepLink";
// NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
// import { Dialog } from "@/lib/utils";
// import PremiumFeatureDialog, { PremiumFeatureDialogProps } from "@/components/ui/PremiumFeatureDialog.vue";
// import { downloadPublicImage } from "@/lib/s3";

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    ListTemplate,
    CharacterList,
    CharacterFolderList,
    CharacterDetail,
    HistoryManageDialog,
    BackToList,
    SpTab,
  },
  props: {
    novelId: String,
    characterId: String,
  },
  data() {
    return {
      isShowFolderList: false,
      isFolderInsertMode: false,
      targetCharacter: null,
      selectedFolderId: null,
      isHistoryManageDialogOpen: false,
    };
  },
  async created() {
    const { novelId } = this;
    await this.$store.dispatch("characterModule/initialize", novelId);
    if (shouldRedirectEmptyListToCreate(this.characterId) && !this.characters.length) {
      this.$router.push({ name: "characterCreate", params: { novelId } });
      return;
    }
    this.openHistoryFromQuery();
  },
  computed: {
    characters() {
      const { $store, selectedFolderId } = this;

      let characters = $store.getters["characterModule/characterList"] as Character[];

      if (selectedFolderId) {
        characters = $store.getters["characterModule/charactersInFolder"](selectedFolderId) as Character[];
      }

      return cloneDeep(characters);
    },
    // ストアの更新（固有名詞モーダルでの一括置換など）に追従できるよう、
    // data() の一度きりのスナップショットではなく computed で都度導出する
    selectedCharacter() {
      const list = this.characters;
      if (!list.length) return null;
      if (this.characterId) {
        return list.find((c) => c.characterId === this.characterId) || null;
      }
      return list[0];
    },
    characterFolder() {
      const { selectedFolderId } = this;
      const characterFolder = this.$store.getters["characterModule/characterFolder"](selectedFolderId);
      return cloneDeep(characterFolder);
    },
    characterFolders() {
      const characterFolders = this.$store.getters["characterModule/characterFolderList"];
      return cloneDeep(characterFolders);
    },
    charactersCount() {
      const characters = this.$store.getters["characterModule/characterList"] as Character[];
      return characters.length;
    },
    plan() {
      return this.$store.getters["billingModule/plan"] as Plan;
    },
    isFree() {
      return this.plan === Plan.free;
    },
  },
  methods: {
    async selectFolder(characterFolderId) {
      this.selectedFolderId = characterFolderId;
      this.isShowFolderList = false;
    },
    async showFolderList() {
      if (this.isFree) {
        this.showPremiumFeatureDialog();
        return;
      }

      this.isShowFolderList = true;
    },
    setFolder(el) {
      if (this.isFree) {
        this.showPremiumFeatureDialog();
        return;
      }

      this.targetCharacter = el.character;
      this.selectedFolderId = el.characterFolder ? el.characterFolder.characterFolderId : null;
      this.isFolderInsertMode = true;
      this.isShowFolderList = true;
    },
    async finishFolderSelect() {
      await this.selectFolder(this.selectedFolderId);

      this.targetCharacter = null;
      this.isFolderInsertMode = false;
      this.isShowFolderList = false;
    },
    async onCharacterRestore() {
      this.isHistoryManageDialogOpen = false;
      await this.$store.dispatch("characterModule/initialize", this.novelId);
    },
    openHistoryFromQuery() {
      if (!hasHistoryQuery(this.$route.query) || !this.selectedCharacter) return;
      this.isHistoryManageDialogOpen = true;
      this.$router.replace({
        name: this.$route.name as string,
        params: this.$route.params,
        query: omitHistoryQuery(this.$route.query) as Record<string, string | (string | null)[]>,
      });
    },
    async showPremiumFeatureDialog() {
      // NOTE: 機能紹介GIFを修正するため一時的にコメントアウト
      // const premiumFeatureDialog = new Dialog(PremiumFeatureDialog);
      // const data: PremiumFeatureDialogProps = {
      //   title: "フォルダ機能はプレミアム機能です",
      //   text: "Nolaプレミアムにご登録いただくと、お好きな名前で資料を管理できる『フォルダ管理機能』をご利用いただけます。”〇〇高校”などの組織ごとの管理や、”味方”、”敵陣”などキャラクターの役割ごとの資料管理ができますので、お手すきの際にぜひご活用いただだけたら幸いです。",
      //   image: (await downloadPublicImage("shared/images/gif/character_folder.gif")) as string,
      // };
      // premiumFeatureDialog.show(data);

      this.$router.push({ name: "subscriptionAnnounce", query: { from: "characterFolder" } });
    },
  },
});

interface Props {
  novelId: string;
  characterId: string;
}

interface Data {
  isShowFolderList: boolean;
  isFolderInsertMode: boolean;
  targetCharacter: Character | null;
  selectedFolderId: string | null;
  isHistoryManageDialogOpen: boolean;
}

interface Computed {
  characters: Character[];
  selectedCharacter: Character | null;
  characterFolder: CharacterFolder;
  characterFolders: CharacterFolder[];
  charactersCount: number;
  plan: Plan;
  isFree: boolean;
}

interface Methods {
  selectFolder(characterFolderId: string): Promise<void>;
  showFolderList(): Promise<void>;
  setFolder(el): void;
  finishFolderSelect(): Promise<void>;
  onCharacterRestore(): Promise<void>;
  showPremiumFeatureDialog(): Promise<void>;
  openHistoryFromQuery(): void;
}
</script>

<style lang="scss" scoped>
.characters-page {
  @include mq(sp) {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    min-height: 0;
    overflow: hidden;

    // タブの高さを考慮してListTemplateの高さを調整
    .list-template {
      flex: 1;
      min-height: 0;
    }
  }
}

.content-main {
  margin: $margin-m;
}
</style>
